import { Injectable, inject } from '@angular/core';
import {
  DEFAULTS,
  FHIR_PUBLISHER,
  OTHER_INPUT_SUBCONTROL_EXT,
  QUESTIONNAIRE_PREFIX,
  SUBCONTROL_DEPENDENCY_ACTION,
} from '../constants/fhir.constants';
import { FHIR_CONVERTER_CONFIG, IControlCard, ITemplateFhirConverter } from '../interfaces/fhir-converter.interfaces';
import { CONTROL_METADATA_PROVIDER } from '../interfaces/metadata-provider.interface';
import {
  CONTROL_TO_FHIR_TYPE_MAP,
  FHIRAnswerOption,
  FHIREnableWhen,
  FHIRExtension,
  FHIRInitialValue,
  FHIRItem,
  FHIRItemType,
  FHIRQuestionnaire,
} from '../models/fhir.model';
import {
  IAnswerOption,
  IConditionalRule,
  IControl,
  ISection,
  ISubsection,
  ITemplateMetadata,
  Template,
} from '../models/template.model';
import { ChoiceOptionLike, isValidChoiceValue } from '../utilities/choice-option-matcher';
import { migrateOtherInputControl, migrateOtherInputTree } from '../../utils/helpers/other-input-migration';
import { FhirExtensionBuilder } from './fhir-extension.builder';
import { LinkIdGenerator } from './link-id.generator';

/**
 * Template FHIR Converter Service
 *
 * Provides bidirectional conversion between the internal Template model and FHIR R4 Questionnaire resources.
 *
 * Note: Not providedIn: 'root' because it needs to be provided in the app context where CONTROL_METADATA_PROVIDER is
 * configured.
 */
@Injectable()
export class TemplateFhirConverter implements ITemplateFhirConverter<Template> {
  private readonly metadataProvider = inject(CONTROL_METADATA_PROVIDER, { optional: true });
  private readonly linkIdGenerator = inject(LinkIdGenerator);
  private readonly extensionBuilder = inject(FhirExtensionBuilder);
  private readonly injectedConfig = inject(FHIR_CONVERTER_CONFIG, { optional: true });

  private readonly qPrefix: string;
  private readonly publisher: string;

  constructor() {
    const config = this.injectedConfig;
    this.qPrefix = config?.questionnairePrefix || QUESTIONNAIRE_PREFIX;
    this.publisher = config?.publisher || FHIR_PUBLISHER;
  }

  // ============================================================================
  // FHIR → TEMPLATE
  // ============================================================================
  /**
   * Convert FHIR Questionnaire to web.template Builder Template model This is specifically for the template builder in
   * web.template package Handles Section > Subsection > Control hierarchy
   *
   * @param questionnaire - FHIRQuestionnaire to convert
   * @returns Template object compatible with BuilderStore
   */
  fhirToBuilderTemplate(questionnaire: FHIRQuestionnaire, template: Template): Template {
    const sectionItems = (questionnaire.item || []).filter((item) => item.type === 'group');
    const sections = sectionItems.map((item, index) => this.fhirSectionItemToBuilderSection(item, index));
    return {
      ...template,
      sections,
    };
  }

  /**
   * Convert a FHIR group item to a builder section Handles extraction of icon from extensions and builds both
   * subsections and controls arrays
   *
   * @param item - FHIR group item
   * @param order - Order in parent
   * @returns Section object with subsections and controls
   */
  private fhirSectionItemToBuilderSection(item: FHIRItem, order: number): any {
    const sectionUi = this.parseUiExtension(item.extension);
    const sectionIcon = typeof sectionUi.icon === 'string' ? sectionUi.icon : 'bounding-box';
    const assessmentStrategy = (sectionUi as any).assessmentStrategy ?? null;
    const rawQuestionTitle = (sectionUi as any).questionTitle;
    const questionTitle =
      typeof rawQuestionTitle === 'string' && rawQuestionTitle.trim() !== '' ? rawQuestionTitle.trim() : null;
    const assessmentPosition = (sectionUi as any).assessmentPosition || 'inside';
    const aiExtraction = (sectionUi as any).aiExtraction ?? (sectionUi as any).isAiExtractionEnabled ?? true;
    const amend = (sectionUi as any).amend ?? true;
    const sectionRequired = (sectionUi as any).sectionRequired === true;
    const extPrefix = this.extensionBuilder.getExtPrefix();
    const tagExt = item.extension?.find((e) => e.url === `${extPrefix}:tag` || e.url?.endsWith(':tag'));
    let tag = (sectionUi as any).tag ?? [];
    if (tagExt?.valueString) {
      try {
        const parsed = JSON.parse(tagExt.valueString);
        if (Array.isArray(parsed)) {
          tag = parsed;
        }
      } catch {
        tag = [tagExt.valueString];
      }
    }

    // Use persisted sectionId from extension if available, otherwise fallback to hash
    const sectionId =
      (sectionUi as any).sectionId || this.stableIdFromString(item.linkId || `section-${order}`, 'section');

    const childItems = Array.isArray(item.item) ? item.item : [];
    const controls = childItems
      .map((child, index) => this.fhirItemToDesignerControl(child, index, { sectionIcon }))
      .filter((c) => c != null);

    // Post-process: reconstruct parent-child relationships from flat subcontrols
    // that have ext:subcontrol-parent marker. This handles the new flat format.
    const processedControls = this.reconstructSubControlHierarchy(controls);

    // Walk the finished tree once so radios/checkboxes nested in tabs and subsections are migrated too.
    migrateOtherInputTree(processedControls, (linkId) => this.stableIdFromString(linkId, 'control'));
    // Extract body image regions from subsection controls' componentProps
    // These are stored in ext:ui during export and parsed back into componentProps
    const bodyImageMappings = this.extractBodyImageMappingsFromControls(processedControls);
    const hasBodyImageMappings = bodyImageMappings.length > 0;

    return {
      id: sectionId,
      linkId: item.linkId,
      title: item.text || `Section ${order + 1}`,
      icon: sectionIcon,
      isVisible: (sectionUi as any).isVisible ?? true,
      pdfExport: (sectionUi as any).pdfExport ?? true,
      aiExtraction,
      amend,
      sectionRequired,
      assessmentStrategy,
      assessmentPosition,
      questionTitle,
      description: undefined,
      order,
      tag,
      enableBodyImage: hasBodyImageMappings,
      bodyImageMappings: hasBodyImageMappings ? bodyImageMappings : undefined,
      subsections: [],
      harmonyProps: {
        dragHandleEnabled: true,
        hasSubSections: processedControls.some((ctrl: any) => Array.isArray(ctrl.children) && ctrl.children.length > 0),
        expandedAll: false,
        canExpand: true,
      },
      controls: processedControls,
    };
  }

  /**
   * Convert FHIR items to designer controls array These are the canvas-view controls with full componentProps from
   * extensions
   *
   * @param items - Array of FHIR items
   * @param parentSectionId - Parent section ID
   * @param sectionExtensionData - Parent section extension data
   * @returns Array of designer controls
   */
  private fhirItemToDesignerControl(
    item: FHIRItem,
    order: number,
    context: { sectionIcon: string }
  ): IControlCard | null {
    if (!item || !item.linkId) {
      return null;
    }

    const uiConfig = this.parseUiExtension(item.extension);
    const dependencies = this.parseDependenciesExtension(item.extension);
    const subcontrolParent = this.extractSubcontrolInfoFromDependencies(dependencies);
    const componentProps = this.uiConfigToComponentProps(uiConfig);

    const inferredType = this.mapFhirItemTypeToDesignerControlType((item.type as FHIRItemType) || 'string');
    // Older Claims templates identify the component but omit the designer controlType.
    // Resolve their registry type before falling back to the generic FHIR choice control.
    const legacyClaimsType =
      uiConfig.component === 'MedClaimsControl' || uiConfig.componentType === 'MedClaimsControl'
        ? 'claims-control'
        : undefined;
    const controlType =
      (uiConfig as any).controlType ||
      ((uiConfig as any).__isTabContent ? '__tab-group' : undefined) ||
      legacyClaimsType ||
      (item.type === 'group' ? 'subsection' : inferredType);

    const id = this.stableIdFromString(item.linkId, 'control');

    const icon =
      (typeof (uiConfig as any).icon === 'string' && (uiConfig as any).icon) ||
      (item.type === 'group' ? context.sectionIcon : this.getIconForControlType(controlType));

    // Backfill icon into componentProps so builder/state code can rely on componentProps as source-of-truth
    if ((componentProps as any).icon === undefined) {
      (componentProps as any).icon = icon;
    }

    const initialValue = this.extractDesignerInitialValue(item);
    const initialAllergies = this.extractDesignerAllergies(item);

    if (initialAllergies && (componentProps as any).allergies === undefined) {
      (componentProps as any).allergies = initialAllergies;
    }

    if (initialValue !== undefined && (componentProps as any).value === undefined) {
      (componentProps as any).value = initialValue;
    }

    // Ensure choice options are restored even if ui-config is missing
    if (
      (item.type === 'choice' || item.type === 'open-choice') &&
      (!Array.isArray((componentProps as any).options) || (componentProps as any).options.length === 0)
    ) {
      (componentProps as any).options = this.buildDesignerOptions(item.answerOption || []);
    }

    // ponytail: Align option values with labels for all choice controls on import
    if (this.isChoiceControl(item.type) && Array.isArray((componentProps as any).options)) {
      (componentProps as any).options = (componentProps as any).options.map((opt: any) => {
        if (opt && typeof opt === 'object') {
          const displayLabel = opt.label || opt.displayText || opt.display || String(opt.value || '');
          const hasMappedCodes = opt.mappedCodes && opt.mappedCodes.length > 0;
          return {
            ...opt,
            value: hasMappedCodes ? opt.value : displayLabel.trim(),
          };
        }
        return opt;
      });
    }

    const requiredFromUi = typeof (uiConfig as any).required === 'boolean' ? (uiConfig as any).required : undefined;
    const required = requiredFromUi ?? item.required ?? false;
    const isRequired = typeof (uiConfig as any).isRequired === 'boolean' ? (uiConfig as any).isRequired : required;

    const isCardLinkGroup = controlType === 'card-link-group';

    if (isCardLinkGroup) {
      const cards = this.extractCardLinkCardsFromChildItems(item.item);
      if (cards.length > 0) {
        (componentProps as any).cards = cards;
      }
    }

    // Determine children: For regular 'group' type, nested items are children.
    // For Card Link Group, nested items represent cards metadata and should NOT be added as canvas children.
    // For 'choice'/'open-choice' types, extract sub-controls tagged with __parentOptionId.
    let children: IControlCard[] | undefined;

    if (item.type === 'group' && Array.isArray(item.item) && !isCardLinkGroup) {
      // For subsection (group type), all nested items are children
      children = item.item
        .map((child, idx) => this.fhirItemToDesignerControl(child, idx, context))
        .filter((c): c is IControlCard => c != null);
      // Post-process: reconstruct subcontrol hierarchy within this group
      // so subcontrols inside subsections are re-nested under their parent choice control
      children = this.reconstructSubControlHierarchy(children);
    } else if (this.isChoiceControl(item.type) && Array.isArray(item.item) && item.item.length > 0) {
      if (controlType === 'conditional-table-grid') {
        // Conditional-table-grid stores cell controls and option sub-controls as flat siblings under the grid item.
        // Convert all children first, then reconstruct parent-child links from subcontrol dependency markers.
        children = item.item
          .map((child, idx) => this.fhirItemToDesignerControl(child, idx, context))
          .filter((c): c is IControlCard => c != null);
        children = this.reconstructSubControlHierarchy(children);
      } else {
        // For choice controls (radio-group, checkbox-group, etc.), extract sub-controls tagged with __parentOptionId
        children = this.extractOptionSubControls(item, context);
      }
    }

    if (controlType === 'tabbed-layout') {
      this.rehydrateTabbedLayoutTabs(componentProps, children);
    }

    const hasOptions = Array.isArray((componentProps as any).options) && (componentProps as any).options.length > 0;
    const optionCount = hasOptions ? (componentProps as any).options.length : 0;

    const questionText = item.text || (componentProps as any).label || '';
    // label must be explicitly set on the root so ROOT_SYNC_PROPS can keep it in sync
    // when the user edits the subsection name through the properties panel or inline label editor.
    // Without this, control.label stays undefined after FHIR load and the FHIR text field
    // never gets the updated value on subsequent edits.
    const labelText = (componentProps as any).label || questionText;

    const extPrefix = this.extensionBuilder.getExtPrefix();
    const tagExt = item.extension?.find((e) => e.url === `${extPrefix}:tag` || e.url?.endsWith(':tag'));
    let controlTag: string[] = (componentProps as any).tag ?? (uiConfig as any).tag ?? [];
    if (tagExt?.valueString) {
      try {
        const parsed = JSON.parse(tagExt.valueString);
        if (Array.isArray(parsed)) {
          controlTag = parsed;
        }
      } catch {
        controlTag = [tagExt.valueString];
      }
    }
    if ((componentProps as any).tag === undefined) {
      (componentProps as any).tag = controlTag;
    }

    // Extract amend property (default to true if not specified)
    const amend = (uiConfig as any).amend ?? (componentProps as any).amend ?? true;

    const control: any = {
      id,
      controlType,
      icon,
      question: questionText,
      label: labelText,
      isRequired,
      hasOptions,
      optionCount,
      hasOther: false,
      componentProps,
      linkId: item.linkId,
      labelColor: (componentProps as any).labelColor,
      subtitleColor: (componentProps as any).subtitleColor,
      amend,
      tag: controlTag,
    };

    if ((uiConfig as any).__conditionalTableGridRowLinkId !== undefined) {
      (control as any).__conditionalTableGridRowLinkId = (uiConfig as any).__conditionalTableGridRowLinkId;
      if (control.componentProps) {
        (control.componentProps as any).__conditionalTableGridRowLinkId = (
          uiConfig as any
        ).__conditionalTableGridRowLinkId;
      }
    }
    if ((uiConfig as any).__conditionalTableGridColId !== undefined) {
      (control as any).__conditionalTableGridColId = (uiConfig as any).__conditionalTableGridColId;
      if (control.componentProps) {
        (control.componentProps as any).__conditionalTableGridColId = (uiConfig as any).__conditionalTableGridColId;
      }
    }

    // Preserve layout hierarchy or choice sub-controls
    if (children && children.length > 0) {
      control.children = children;
    }

    // Sync questionTitle onto control root if present in componentProps or uiConfig
    const rawCtrlQuestionTitle =
      (componentProps as any).questionTitle !== undefined
        ? (componentProps as any).questionTitle
        : (uiConfig as any).questionTitle;
    if (typeof rawCtrlQuestionTitle === 'string' && rawCtrlQuestionTitle.trim() !== '') {
      control.questionTitle = rawCtrlQuestionTitle.trim();
      (componentProps as any).questionTitle = rawCtrlQuestionTitle.trim();
    } else if (rawCtrlQuestionTitle !== undefined || controlType === 'subsection') {
      control.questionTitle = null;
      (componentProps as any).questionTitle = null;
    }

    // Recreate tab-group marker so DesignerFhirConverter can export it again
    if ((uiConfig as any).__isTabContent === true) {
      control.__tabGroup = true;
      control.tabKey = (uiConfig as any).tabKey || 'tab';
      if (typeof (componentProps as any).questionTitle === 'string') {
        control.questionTitle = (componentProps as any).questionTitle;
      }
    }

    // Restore dependencies (custom logic) exactly as exported
    if (dependencies) {
      control.fhirPath = dependencies;
      if ((control.componentProps as any).fhirPath === undefined) {
        (control.componentProps as any).fhirPath = dependencies;
      }
    }

    // Store subcontrol-parent marker for post-processing in reconstructSubControlHierarchy()
    if (subcontrolParent) {
      control.__subcontrolParent = subcontrolParent;
    }

    // Tag as the auto-generated "Other" text-input subcontrol so reconstruction can wire it back to its parent.
    if (item.extension?.some((e) => e.url === OTHER_INPUT_SUBCONTROL_EXT && e.valueBoolean === true)) {
      (control as any).__isOtherInput = true;
    }

    // Mirror common validation props to root if present (builder frequently expects these)
    this.copyCommonValidationPropsToRoot(control);

    // Restore initial values to root as well (DesignerFhirConverter checks both root and componentProps)
    if (initialAllergies) {
      control.allergies = initialAllergies;
    }
    if (initialValue !== undefined) {
      control.value = initialValue;
    }

    // Validate choice control value against active options and special options.
    // If stale or not found in options, remove it so legacy slugs (e.g. 'option_1') do not linger.
    if (this.isChoiceControl(item.type)) {
      const card = control as IControlCard;
      const cProps = card.componentProps as Record<string, unknown> | undefined;
      const val = cProps?.['value'] !== undefined ? cProps['value'] : card.value;
      const opts = (cProps?.['options'] ?? card.options ?? []) as ChoiceOptionLike[];
      if (val !== undefined && val !== null && val !== '' && !isValidChoiceValue(val, opts, card)) {
        if (cProps && 'value' in cProps) {
          delete cProps['value'];
        }
        delete (card as Record<string, unknown>)['value'];
      }
    }

    return control as IControlCard;
  }

  /** Parse Card Link card child items (`parent.__card-*`) into stage card rows for componentProps.cards. */
  private extractCardLinkCardsFromChildItems(items: FHIRItem[] | undefined): Array<Record<string, unknown>> {
    if (!Array.isArray(items) || items.length === 0) {
      return [];
    }

    const cards: Array<Record<string, unknown>> = [];

    for (const item of items) {
      if (!item?.linkId || !item.linkId.includes('.__card-')) {
        continue;
      }

      const cardUi = this.parseUiExtension(item.extension);
      const cardId = String(
        (cardUi as any).cardId ?? item.linkId.substring(item.linkId.lastIndexOf('.__card-') + '.__card-'.length)
      );
      const title = String((cardUi as any).title ?? '').trim();
      const linkUrl = String((cardUi as any).linkUrl ?? '').trim();

      if (!title || !linkUrl) {
        continue;
      }

      const linkTarget = String((cardUi as any).linkTarget ?? '').trim();
      cards.push({
        id: cardId,
        title,
        questionTitle: String((cardUi as any).questionTitle ?? '').trim(),
        subtitle: String((cardUi as any).subtitle ?? '').trim(),
        linkUrl,
        linkLabel: String((cardUi as any).linkLabel ?? '').trim() || 'View Resource',
        notesPlaceholder: String((cardUi as any).notesPlaceholder ?? '').trim() || 'Notes',
        defaultToggleState: (cardUi as any).defaultToggleState,
        notesRequiredWhenToggleOn: (cardUi as any).notesRequiredWhenToggleOn,
        openLinkIn: linkTarget === 'same-tab' ? 'same-tab' : linkTarget === 'new-tab' ? 'new-tab' : undefined,
      });
    }

    return cards;
  }

  private mapFhirItemTypeToDesignerControlType(type: FHIRItemType): string {
    const typeMapping: Record<string, string> = {
      string: 'text-input',
      text: 'text-area',
      integer: 'number-input',
      decimal: 'decimal-input',
      date: 'date-picker',
      dateTime: 'datetime-picker',
      time: 'time-picker',
      boolean: 'checkbox',
      choice: 'radio-group',
      'open-choice': 'multi-select',
      display: 'display-text',
    };
    return typeMapping[type] || 'text-input';
  }

  private extractDesignerInitialValue(item: FHIRItem): unknown {
    if (!Array.isArray(item.initial) || item.initial.length === 0) {
      return undefined;
    }

    // DesignerFhirConverter sets radio-group initial via valueCoding
    const first: any = item.initial[0] as any;
    if (first?.valueCoding?.code !== undefined) {
      return first.valueCoding.code;
    }

    // If there are multiple initial values, prefer returning an array
    if (item.initial.length > 1) {
      const values = item.initial.map((v) => this.extractInitialValue(v)).filter((v) => v !== undefined);
      return values.length > 0 ? values : undefined;
    }

    // Fallback to generic extraction
    const firstValue = item.initial[0];
    return firstValue ? this.extractInitialValue(firstValue) : undefined;
  }

  private extractDesignerAllergies(item: FHIRItem): Array<{ name: string; reaction?: string }> | undefined {
    if (!Array.isArray(item.initial) || item.initial.length === 0) {
      return undefined;
    }

    // DesignerFhirConverter exports add-allergy as an array of valueString entries
    const strings = item.initial
      .map((v) => (v as any)?.valueString)
      .filter((v): v is string => typeof v === 'string' && v.length > 0);

    if (strings.length < 2) {
      return undefined;
    }

    return strings.map((text) => {
      const parts = text.split(' - ');
      const name = parts[0] || text;
      const reaction = parts.length > 1 ? parts.slice(1).join(' - ') : undefined;
      return reaction ? { name, reaction } : { name };
    });
  }

  private parseDependenciesExtension(extensions: FHIRExtension[] | undefined): string | undefined {
    if (!Array.isArray(extensions)) {
      return undefined;
    }

    const url = `${this.extensionBuilder.getExtPrefix()}:dependencies`;
    const ext = extensions.find((e) => e.url === url);
    return ext?.valueString || undefined;
  }

  /**
   * Extract subcontrol parent info from the dependencies extension. Looks for an entry with Action: 'subcontrol' — the
   * unambiguous structural marker emitted by DesignerFhirConverter. The field property is parentLinkId, value is
   * optionValue.
   */
  private extractSubcontrolInfoFromDependencies(
    dependencies: string | undefined
  ): { parentLinkId: string; optionValue: string } | undefined {
    if (!dependencies) return undefined;
    try {
      const parsed = JSON.parse(dependencies);
      let rules: any[] = [];
      if (Array.isArray(parsed)) {
        rules = parsed;
      } else if (parsed && Array.isArray(parsed.conditionalLogic)) {
        rules = parsed.conditionalLogic;
      } else if (parsed) {
        rules = [parsed];
      }

      const subcontrolRule = rules.find((r: any) => String(r.Action).toLowerCase() === SUBCONTROL_DEPENDENCY_ACTION);
      if (!subcontrolRule?.Rules?.length) return undefined;
      const rule = subcontrolRule.Rules[0];
      if (typeof rule.field === 'string' && rule.value !== undefined) {
        return { parentLinkId: rule.field, optionValue: String(rule.value) };
      }
    } catch {
      // ignore parse errors
    }
    return undefined;
  }

  private uiConfigToComponentProps(uiConfig: Record<string, any>): Record<string, any> {
    const omit = new Set([
      'component',
      'componentType',
      'package',
      'category',
      'controlType',
      'linkId',
      'isLayoutControl',
      '__isTabContent',
      'tabKey',
    ]);

    const props: Record<string, any> = {};
    for (const [key, value] of Object.entries(uiConfig || {})) {
      if (omit.has(key)) continue;
      props[key] = value;
    }
    return props;
  }

  private rehydrateTabbedLayoutTabs(componentProps: Record<string, any>, children?: IControlCard[]): void {
    if (!Array.isArray(children) || children.length === 0) {
      return;
    }

    const tabGroups = children.filter(
      (child) => (child as any).__tabGroup === true && typeof (child as any).tabKey === 'string'
    );

    if (tabGroups.length === 0) {
      return;
    }

    const metadataByKey = new Map<string, { key: string; label: string; questionTitle?: string }>();
    for (const tabGroup of tabGroups) {
      const tabKey = String((tabGroup as any).tabKey || '').trim();
      if (!tabKey) {
        continue;
      }

      const tabLabel = String(tabGroup.label || (tabGroup.componentProps as any)?.label || '').trim();
      const tabQuestionTitle = (tabGroup.componentProps as any)?.questionTitle;

      const tabMetadata: { key: string; label: string; questionTitle?: string } = {
        key: tabKey,
        label: tabLabel || tabKey,
      };
      if (typeof tabQuestionTitle === 'string') {
        tabMetadata.questionTitle = tabQuestionTitle;
      }

      metadataByKey.set(tabKey, tabMetadata);
    }

    const configuredTabs = Array.isArray(componentProps.tabs) ? componentProps.tabs : [];

    if (configuredTabs.length === 0) {
      componentProps.tabs = Array.from(metadataByKey.values()).map((tab) => ({
        key: tab.key,
        label: tab.label,
        ...(Object.prototype.hasOwnProperty.call(tab, 'questionTitle') ? { questionTitle: tab.questionTitle } : {}),
      }));
      return;
    }

    const seenKeys = new Set<string>();
    const mergedTabs = configuredTabs
      .map((tab: any) => {
        const tabKey = String(tab?.key || '').trim();
        if (!tabKey) {
          return null;
        }

        seenKeys.add(tabKey);
        const groupMeta = metadataByKey.get(tabKey);
        if (!groupMeta) {
          return {
            ...tab,
            key: tabKey,
          };
        }

        return {
          ...tab,
          key: tabKey,
          label: groupMeta.label || String(tab?.label || '').trim() || tabKey,
          ...(Object.prototype.hasOwnProperty.call(groupMeta, 'questionTitle')
            ? { questionTitle: groupMeta.questionTitle }
            : {}),
        };
      })
      .filter((tab: any) => tab !== null);

    for (const [tabKey, groupMeta] of metadataByKey.entries()) {
      if (seenKeys.has(tabKey)) {
        continue;
      }

      mergedTabs.push({
        key: tabKey,
        label: groupMeta.label,
        ...(Object.prototype.hasOwnProperty.call(groupMeta, 'questionTitle')
          ? { questionTitle: groupMeta.questionTitle }
          : {}),
      });
    }

    componentProps.tabs = mergedTabs;
  }

  private stableIdFromString(input: string, prefix: string): string {
    const normalized = String(input || '');
    let hash = 5381;
    for (let i = 0; i < normalized.length; i++) {
      hash = (hash * 33) ^ normalized.charCodeAt(i);
    }
    // Convert to unsigned 32-bit and base36 for compactness
    const suffix = (hash >>> 0).toString(36);
    return `${prefix}-${suffix}`;
  }

  private copyCommonValidationPropsToRoot(control: any): void {
    const props = control?.componentProps;
    if (!props || typeof props !== 'object') {
      return;
    }

    const keys = ['required', 'minlength', 'maxlength', 'min', 'max', 'readonly', 'pattern'];
    for (const key of keys) {
      if (props[key] !== undefined && control[key] === undefined) {
        control[key] = props[key];
      }
    }
  }

  /**
   * Build designer-style options from FHIR answer options
   *
   * @param answerOptions - FHIR answer options
   * @returns Designer-style options
   */
  private buildDesignerOptions(answerOptions: FHIRAnswerOption[]): any[] {
    return answerOptions
      .map((option) => {
        if (option.valueCoding) {
          const designerOption: any = {
            id: option.valueCoding.code,
            label: option.valueCoding.display || option.valueCoding.code,
            value: option.valueCoding.code,
          };

          return designerOption;
        } else if (option.valueString) {
          return {
            id: option.valueString,
            label: option.valueString,
            value: option.valueString,
          };
        }
        return null;
      })
      .filter((opt) => opt !== null);
  }

  /**
   * Parse UI extension to extract componentProps
   *
   * @param extensions - FHIR extensions
   * @returns Parsed UI config
   */
  private parseUiExtension(extensions: FHIRExtension[] | undefined): any {
    if (!Array.isArray(extensions)) {
      return {};
    }

    const uiUrl = this.extensionBuilder.getUiConfigExt();
    const uiExt = extensions.find((ext) => ext.url === uiUrl && typeof ext.valueString === 'string');
    if (uiExt?.valueString) {
      try {
        return JSON.parse(uiExt.valueString);
      } catch {
        return {};
      }
    }

    // Backward-compatible fallback: any extension URL containing ':ui'
    for (const ext of extensions) {
      if (ext.url && ext.url.includes(':ui') && ext.valueString) {
        try {
          return JSON.parse(ext.valueString);
        } catch {
          return {};
        }
      }
    }

    return {};
  }

  /**
   * Extract body image mappings from subsection controls' componentProps When FHIR is parsed, body image regions are
   * stored in ext:ui of subsection items. This method collects them from all subsection controls into a flat array
   * suitable for the section-level bodyImageMappings field.
   */
  private extractBodyImageMappingsFromControls(controls: any[]): any[] {
    const mappings: any[] = [];

    for (const control of controls) {
      if (control?.controlType === 'subsection') {
        const regions = control?.componentProps?.bodyImageRegions || control?.bodyImageRegions;
        if (Array.isArray(regions) && regions.length > 0) {
          mappings.push(...regions);
        }
      }
    }

    return mappings;
  }

  /**
   * Get icon for control type
   *
   * @param controlType - Designer control type
   * @returns Icon name
   */
  private getIconForControlType(controlType: string): string {
    const iconMap: Record<string, string> = {
      'text-input': 'ccs-input-cursor-text',
      'text-area': 'ccs-input-text',
      'number-input': 'ccs-input-numbers',
      'decimal-input': 'ccs-input-numbers',
      'date-picker': 'ccs-calendar-range',
      'datetime-picker': 'ccs-calendar-clock',
      'time-picker': 'ccs-clock',
      checkbox: 'ccs-square-check',
      'radio-group': 'ccs-circle',
      'multi-select': 'ccs-list-check',
      'display-text': 'ccs-file-text',
    };

    return iconMap[controlType] || 'ccs-component';
  }

  private sectionToItem(section: ISection): FHIRItem {
    const linkId = this.linkIdGenerator.generateFromTitle(section.title);

    return {
      linkId,
      type: 'group',
      text: section.title,
      item: section.subsections.map((sub) => this.subsectionToItem(sub, linkId)),
      extension: this.buildSectionExtensions(section),
    };
  }

  private itemToSection(item: FHIRItem, order: number): ISection {
    const sectionUi = this.parseUiExtension(item.extension);
    return {
      id: this.linkIdGenerator.generateUUID(),
      title: item.text || `Section ${order + 1}`,
      icon: this.extensionBuilder.extractExtensionValue(item.extension, 'icon') || sectionUi.icon,
      color: this.extensionBuilder.extractExtensionValue(item.extension, 'color') || sectionUi.color,
      description: this.extensionBuilder.extractExtensionValue(item.extension, 'desc') || sectionUi.description,
      subsections: item.item?.map((sub, idx) => this.itemToSubsection(sub, idx)) || [],
      order,
      harmonyProps: this.extensionBuilder.extractHarmonyProps(item.extension),
      questionTitle:
        this.extensionBuilder.extractExtensionValue(item.extension, 'questionTitle') ||
        (sectionUi as any).questionTitle ||
        null,
    };
  }

  // ============================================================================
  // SUBSECTION CONVERSION
  // ============================================================================

  private subsectionToItem(subsection: ISubsection, parentLinkId: string): FHIRItem {
    const baseName = this.linkIdGenerator.generateFromTitle(subsection.title);
    const linkId = `${parentLinkId}.${baseName}`;

    const item: FHIRItem = {
      linkId,
      type: 'group',
      text: subsection.title,
      item: subsection.items.map((ctrl) => this.controlToItem(ctrl, linkId)),
    };

    if (subsection.description) {
      item.extension = [
        {
          url: `${this.extensionBuilder.getExtPrefix()}:desc`,
          valueString: subsection.description,
        },
      ];
    }

    return item;
  }

  private itemToSubsection(item: FHIRItem, order: number): ISubsection {
    return {
      id: this.linkIdGenerator.generateUUID(),
      title: item.text || `Subsection ${order + 1}`,
      description: this.extensionBuilder.extractExtensionValue(item.extension, 'desc'),
      items: item.item?.map((ctrl, idx) => this.itemToControl(ctrl, idx)) || [],
      order,
      parentSectionId: '',
    };
  }

  // ============================================================================
  // CONTROL CONVERSION
  // ============================================================================

  private controlToItem(control: IControl, parentLinkId: string): FHIRItem {
    const controlName =
      control.id ||
      control.label
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') ||
      'control';
    const uniqueSlug = this.linkIdGenerator.generateSlug();
    const linkId = `${parentLinkId}.${controlName}-${uniqueSlug}`;
    const fhirType = this.mapControlToFhirType(control.controlType);

    const item: FHIRItem = {
      linkId,
      type: fhirType,
      text: control.label,
      required: control.required,
      readOnly: control.readOnly,
      extension: this.buildControlExtensions(control),
    };

    if (control.answerOptions?.length) {
      item.answerOption = control.answerOptions.map((opt) => this.answerOptionToFhir(opt));
    }

    if (control.conditionalRules?.length) {
      item.enableWhen = control.conditionalRules.map((rule) => this.ruleToEnableWhen(rule));
    }

    if (control.defaultValue !== undefined) {
      item.initial = [this.createInitialValue(fhirType, control.defaultValue)];
    }

    return item;
  }

  private itemToControl(item: FHIRItem, order: number): IControl {
    return {
      id: this.linkIdGenerator.generateUUID(),
      controlType: this.mapFhirToControlType(item.type),
      label: item.text || '',
      placeholder: this.extensionBuilder.extractExtensionValue(item.extension, 'ph'),
      helpText: this.extensionBuilder.extractExtensionValue(item.extension, 'help'),
      required: item.required || false,
      readOnly: item.readOnly,
      defaultValue: item.initial?.[0] ? this.extractInitialValue(item.initial[0]) : undefined,
      answerOptions: item.answerOption?.map((opt, idx) => this.fhirToAnswerOption(opt, idx)),
      order,
      parentId: '',
    };
  }

  // ============================================================================
  // TYPE MAPPING
  // ============================================================================

  private mapControlToFhirType(controlType: string): FHIRItemType {
    const metadata = this.metadataProvider?.getMetadata(controlType);
    return (metadata?.fhirType as FHIRItemType) || CONTROL_TO_FHIR_TYPE_MAP[controlType] || 'string';
  }

  private mapFhirToControlType(fhirType: FHIRItemType): string {
    // Try basic controls first
    const basicControls = this.metadataProvider?.getPaletteData(DEFAULTS.BASIC_CATEGORY);
    const basicMatch = basicControls?.find((c) => {
      const meta = this.metadataProvider?.getMetadata(c.controlType);
      return meta?.fhirType === fhirType;
    });

    if (basicMatch) return basicMatch.controlType;

    // Search all controls
    const allControls = this.metadataProvider?.getPaletteData();
    const anyMatch = allControls?.find((c) => {
      const meta = this.metadataProvider?.getMetadata(c.controlType);
      return meta?.fhirType === fhirType;
    });

    return anyMatch?.controlType || DEFAULTS.CONTROL_TYPE;
  }

  // ============================================================================
  // ANSWER OPTIONS
  // ============================================================================

  private answerOptionToFhir(option: IAnswerOption): FHIRAnswerOption {
    return {
      valueCoding: {
        code: option.code,
        display: option.display,
      },
    };
  }

  private fhirToAnswerOption(option: FHIRAnswerOption, order: number): IAnswerOption {
    return {
      id: this.linkIdGenerator.generateUUID(),
      code: option.valueCoding?.code || '',
      display: option.valueCoding?.display || '',
      order,
    };
  }

  // ============================================================================
  // CONDITIONAL RULES
  // ============================================================================

  private ruleToEnableWhen(rule: IConditionalRule): FHIREnableWhen {
    const enableWhen: FHIREnableWhen = {
      question: rule.sourceControlId,
      operator: this.mapOperator(rule.operator),
    };

    if (typeof rule.value === 'boolean') {
      enableWhen.answerBoolean = rule.value;
    } else if (typeof rule.value === 'number') {
      enableWhen.answerInteger = rule.value;
    } else {
      enableWhen.answerString = String(rule.value);
    }

    return enableWhen;
  }

  private mapOperator(operator: string): FHIREnableWhen['operator'] {
    const map: Record<string, FHIREnableWhen['operator']> = {
      equals: '=',
      notEquals: '!=',
      greaterThan: '>',
      lessThan: '<',
      contains: 'contains',
    };
    return map[operator] || '=';
  }

  // ============================================================================
  // INITIAL VALUES
  // ============================================================================

  private createInitialValue(type: FHIRItemType, value: unknown): FHIRInitialValue {
    const initial: FHIRInitialValue = {};

    switch (type) {
      case 'boolean':
        initial.valueBoolean = Boolean(value);
        break;
      case 'integer':
        initial.valueInteger = Number(value);
        break;
      case 'decimal':
        initial.valueDecimal = Number(value);
        break;
      case 'date':
        initial.valueDate = String(value);
        break;
      case 'dateTime':
        initial.valueDateTime = String(value);
        break;
      case 'time':
        initial.valueTime = String(value);
        break;
      default:
        initial.valueString = String(value);
    }

    return initial;
  }

  private extractInitialValue(initial: FHIRInitialValue): unknown {
    if (initial.valueBoolean !== undefined) return initial.valueBoolean;
    if (initial.valueInteger !== undefined) return initial.valueInteger;
    if (initial.valueDecimal !== undefined) return initial.valueDecimal;
    if (initial.valueDate !== undefined) return initial.valueDate;
    if (initial.valueDateTime !== undefined) return initial.valueDateTime;
    if (initial.valueTime !== undefined) return initial.valueTime;
    if (initial.valueString !== undefined) return initial.valueString;
    return undefined;
  }

  // ============================================================================
  // EXTENSION BUILDING
  // ============================================================================

  private buildSectionExtensions(section: ISection): import('../models/fhir.model').FHIRExtension[] {
    const ext = this.extensionBuilder.getExtPrefix();
    const extensions: import('../models/fhir.model').FHIRExtension[] = [];

    if (section.icon) {
      extensions.push({ url: `${ext}:icon`, valueString: section.icon });
    }
    if (section.color) {
      extensions.push({ url: `${ext}:color`, valueString: section.color });
    }
    if (section.description) {
      extensions.push({ url: `${ext}:desc`, valueString: section.description });
    }

    return extensions;
  }

  private buildControlExtensions(control: IControl): import('../models/fhir.model').FHIRExtension[] {
    const ext = this.extensionBuilder.getExtPrefix();
    const extensions: import('../models/fhir.model').FHIRExtension[] = [];

    if (control.placeholder) {
      extensions.push({ url: `${ext}:ph`, valueString: control.placeholder });
    }
    if (control.helpText) {
      extensions.push({ url: `${ext}:help`, valueString: control.helpText });
    }

    return extensions;
  }

  // ============================================================================
  // METADATA EXTRACTION
  // ============================================================================

  private extractMetadata(questionnaire: FHIRQuestionnaire): ITemplateMetadata {
    const metadata: ITemplateMetadata = {};

    if (!questionnaire.extension) return metadata;

    for (const ext of questionnaire.extension) {
      if (ext.url.endsWith('chg') || ext.url.endsWith('changeCount')) {
        metadata.changeCount = ext.valueInteger;
      } else if (ext.url.endsWith('cBy') || ext.url.endsWith('createdBy')) {
        metadata.createdBy = ext.valueString;
      } else if (ext.url.endsWith('mBy') || ext.url.endsWith('lastModifiedBy')) {
        metadata.lastModifiedBy = ext.valueString;
      } else if (ext.url.endsWith('pAt') || ext.url.endsWith('publishedAt')) {
        metadata.publishedAt = ext.valueString ? new Date(ext.valueString) : undefined;
      } else if (ext.url.endsWith('pBy') || ext.url.endsWith('publishedBy')) {
        metadata.publishedBy = ext.valueString;
      }
    }

    return metadata;
  }

  /**
   * Reconstruct parent-child relationships from flat subcontrols that have the `urn:ccs:ext:subcontrol-parent` marker
   * extension. This is the new format where subcontrols are flat siblings with dependency rules instead of nested
   * items.
   *
   * Post-processing pass:
   *
   * 1. Scan all controls for subcontrol-parent markers
   * 2. Move marked controls from flat list into parent's children[]
   * 3. Set __parentOptionId on the control for the designer
   * 4. Strip auto-generated dependency rule from fhirPath (designer doesn't need to show it)
   */
  private reconstructSubControlHierarchy(controls: IControlCard[]): IControlCard[] {
    // Build lookup of controls by linkId for parent resolution
    const controlByLinkId = new Map<string, IControlCard>();
    for (const ctrl of controls) {
      if ((ctrl as any).linkId) {
        controlByLinkId.set((ctrl as any).linkId, ctrl);
      }
    }

    const subControlIndices = new Set<number>();

    // First pass: identify subcontrols and attach to parents
    for (let i = 0; i < controls.length; i++) {
      const ctrl = controls[i];
      if (!ctrl) {
        continue;
      }
      const subParentInfo = (ctrl as any).__subcontrolParent;

      if (!subParentInfo) {
        continue;
      }

      const { parentLinkId, optionValue } = subParentInfo;
      const parent = controlByLinkId.get(parentLinkId);

      if (!parent) {
        // Parent not found — leave as flat control (graceful degradation)
        continue;
      }

      // Set __parentOptionId on the control for the designer
      // ponytail: Align subcontrol __parentOptionId with parent's display label for compatibility.
      const normalize = (s: string) =>
        String(s || '')
          .toLowerCase()
          .replace(/[_-]/g, ' ')
          .trim();
      const parentOptions = (parent.componentProps as any)?.options || [];
      const matchingOpt = parentOptions.find(
        (opt: any) => normalize(opt.value) === normalize(optionValue) || normalize(opt.label) === normalize(optionValue)
      );
      const resolvedOptionValue = matchingOpt ? String(matchingOpt.value).trim() : optionValue;

      (ctrl as any).__parentOptionId = resolvedOptionValue;
      if (ctrl.componentProps) {
        (ctrl.componentProps as any).__parentOptionId = resolvedOptionValue;
      }

      // Set __parentLinkId so the serializer can build dependency rules
      (ctrl as any).__parentLinkId = parentLinkId;
      if (ctrl.componentProps) {
        (ctrl.componentProps as any).__parentLinkId = parentLinkId;
      }

      // Strip auto-generated dependency from fhirPath so visibility dialog doesn't show it
      this.stripSubcontrolDependency(ctrl);

      // Add to parent's children array
      if (!parent.children) {
        parent.children = [];
      }
      parent.children.push(ctrl);

      // Legacy-compatible: expose the auto "Other" text-input via hasOther + otherInputLinkId on the parent,
      // in addition to nesting it under children like any other subcontrol.
      if ((ctrl as any).__isOtherInput) {
        parent.hasOther = true;
        if (parent.componentProps) {
          (parent.componentProps as any).hasOther = true;
          (parent.componentProps as any).showOtherInput = true;
        }
        parent.otherInputLinkId = (ctrl as any).linkId;
      }

      // Mark for removal from flat list
      subControlIndices.add(i);

      // Clean up the marker
      delete (ctrl as any).__subcontrolParent;
    }

    // Second pass: remove subcontrols from flat list (they're now in parent.children)
    const remaining = controls.filter((_, idx) => !subControlIndices.has(idx));

    // Bring radio/checkbox "Other" inputs from older templates up to the current child-control format.
    for (const ctrl of remaining) {
      migrateOtherInputControl(ctrl, (linkId) => this.stableIdFromString(linkId, 'control'));
    }

    return remaining;
  }

  /**
   * Strip the auto-generated subcontrol dependency rule from a control's fhirPath. Removes any entry with Action:
   * 'subcontrol' — these are structural markers, not user-authored rules, so the visibility dialog should never show
   * them.
   */
  private stripSubcontrolDependency(control: IControlCard): void {
    const fhirPath = (control as any).fhirPath;
    if (!fhirPath || typeof fhirPath !== 'string') {
      return;
    }

    try {
      const parsed = JSON.parse(fhirPath);
      let isArray = Array.isArray(parsed);
      let rules: any[] = [];
      if (isArray) {
        rules = parsed;
      } else if (parsed && Array.isArray(parsed.conditionalLogic)) {
        rules = parsed.conditionalLogic;
      } else if (parsed) {
        rules = [parsed];
      }

      // Remove any rule with Action: 'subcontrol'
      const remaining = rules.filter((rule: any) => String(rule.Action).toLowerCase() !== SUBCONTROL_DEPENDENCY_ACTION);

      if (remaining.length === 0) {
        delete (control as any).fhirPath;
        if (control.componentProps) {
          delete (control.componentProps as any).fhirPath;
        }
      } else {
        let newFhirPath: string;
        if (isArray) {
          newFhirPath = JSON.stringify(remaining);
        } else {
          newFhirPath = JSON.stringify({
            ...parsed,
            conditionalLogic: remaining,
            dependencyCount: remaining.length,
          });
        }
        (control as any).fhirPath = newFhirPath;
        if (control.componentProps) {
          (control.componentProps as any).fhirPath = newFhirPath;
        }
      }
    } catch {
      // If parse fails, leave fhirPath as-is
    }
  }

  /**
   * Check if a FHIR item type is a choice control (radio-group, checkbox-group, etc.) These controls can have
   * sub-controls nested in item.item, tagged with __parentOptionId
   */
  private isChoiceControl(itemType?: string): boolean {
    return itemType === 'choice' || itemType === 'open-choice';
  }

  /**
   * Extract sub-controls from a choice control's nested items (BACKWARD COMPAT - old format). Sub-controls are
   * identified by having __parentOptionId in their ui-config extension. These were nested by the old
   * DesignerFhirConverter format and need to be reconstructed here. Also sets __parentLinkId so the new serializer can
   * build dependency rules.
   *
   * Also handles conditional-table-grid cell controls tagged with __conditionalTableGridRowLinkId and
   * __conditionalTableGridColId.
   */
  private extractOptionSubControls(parentItem: FHIRItem, context: { sectionIcon: string }): IControlCard[] {
    const subControls: IControlCard[] = [];

    if (!Array.isArray(parentItem.item)) {
      return subControls;
    }

    for (let idx = 0; idx < parentItem.item.length; idx++) {
      const item = parentItem.item[idx];
      if (!item) {
        continue;
      }

      // Check if this item is a sub-control (has __parentOptionId in extension)
      const uiConfig = this.parseUiExtension(item.extension);
      const parentOptionId = (uiConfig as any).__parentOptionId;
      const rowLinkId = (uiConfig as any).__conditionalTableGridRowLinkId;
      const colId = (uiConfig as any).__conditionalTableGridColId;

      if (parentOptionId !== undefined) {
        // This is a sub-control, convert it to designer control
        const subControl = this.fhirItemToDesignerControl(item, idx, context);
        if (subControl) {
          // ponytail: Align subcontrol __parentOptionId with parent's display label for compatibility.
          const normalize = (s: string) =>
            String(s || '')
              .toLowerCase()
              .replace(/[_-]/g, ' ')
              .trim();
          const parentUiConfig = this.parseUiExtension(parentItem.extension);
          const parentOptions: any[] = [
            ...(parentUiConfig?.options && Array.isArray(parentUiConfig.options) ? parentUiConfig.options : []),
            ...this.buildDesignerOptions(parentItem.answerOption || []),
          ];
          const matchingOpt = parentOptions.find(
            (opt: any) =>
              normalize(opt.value) === normalize(parentOptionId) || normalize(opt.label) === normalize(parentOptionId)
          );
          const resolvedOptionValue = matchingOpt
            ? (matchingOpt.label || String(matchingOpt.value)).trim()
            : parentOptionId;

          // CRITICAL: Set __parentOptionId on the control object so the builder can find it
          (subControl as any).__parentOptionId = resolvedOptionValue;
          // Also set in componentProps for consistency with how controls are created in the builder
          if (subControl.componentProps) {
            (subControl.componentProps as any).__parentOptionId = resolvedOptionValue;
          }
          // Set __parentLinkId so new serializer can build dependency rules
          (subControl as any).__parentLinkId = parentItem.linkId;
          if (subControl.componentProps) {
            (subControl.componentProps as any).__parentLinkId = parentItem.linkId;
          }
          subControls.push(subControl);
        }
      } else if (rowLinkId !== undefined && colId !== undefined) {
        // This is a conditional-table-grid cell control
        const subControl = this.fhirItemToDesignerControl(item, idx, context);
        if (subControl) {
          (subControl as any).__conditionalTableGridRowLinkId = rowLinkId;
          (subControl as any).__conditionalTableGridColId = colId;
          if (subControl.componentProps) {
            (subControl.componentProps as any).__conditionalTableGridRowLinkId = rowLinkId;
            (subControl.componentProps as any).__conditionalTableGridColId = colId;
          }
          subControls.push(subControl);
        }
      }
    }

    return subControls;
  }
}
